/**
 * Scene Custom Layers — DreamMee
 * Extends SceneComposer with dynamic custom layer management.
 * Loaded AFTER step-environment.js.
 *
 * Designs with `customLayers.enabled: true` allow users to add/remove
 * overlay layer instances from a pool of allowed group types.
 * Instance keys use the format `{groupId}:{index}`, e.g. 'box:0', 'box_car:1'.
 */

Object.assign(SceneComposer.prototype, {

  /**
   * Initialize custom layers from design config.
   * Called from the constructor after base state is set up.
   */
  _initCustomLayers() {
    const cfg = this.design && this.design.customLayers;
    this._customLayersEnabled = !!(cfg && cfg.enabled);
    this._customInstances = [];   // flat array of { key, groupId }
    this._customNextId = 0;
    this._customMaxCount = (cfg && cfg.maxCount) || 10;
    this._customAllowedGroups = (cfg && cfg.allowedGroups) || [];
    this._customTemplateGroups = this.layerGroups.filter(g => g.isCustomTemplate);

    if (!this._customLayersEnabled) return;

    // Create default instances
    const defaults = (cfg && cfg.defaults) || [];
    for (const def of defaults) {
      const key = this._createCustomInstance(def.group, def.variant, true);
      if (key && def.defaultPremadeId) {
        this._setInitialPremadeFromConfig(key, def.group, def.defaultPremadeId);
      }
    }
  },

  /**
   * Resolve a layerGroup definition from a key that may be an instance key.
   * @param {string} key - e.g. 'box:2' or 'background'
   * @returns {{ groupDef, isCustom, baseGroupId }}
   */
  _resolveGroupDef(key) {
    // Direct match first
    const direct = this.layerGroups.find(g => g.id === key);
    if (direct) return { groupDef: direct, isCustom: false, baseGroupId: key };

    // Instance key pattern: "groupId:index"
    const colonIdx = key.indexOf(':');
    if (colonIdx > 0) {
      const baseId = key.substring(0, colonIdx);
      const def = this.layerGroups.find(g => g.id === baseId && g.isCustomTemplate);
      if (def) return { groupDef: def, isCustom: true, baseGroupId: baseId };
    }
    return { groupDef: null, isCustom: false, baseGroupId: key };
  },

  /**
   * Internal: create a custom instance without triggering renders.
   * Used during init and by addCustomLayer().
   */
  _createCustomInstance(groupId, variantId, skipRender) {
    const group = this.layerGroups.find(g => g.id === groupId);
    if (!group || !group.isCustomTemplate) return null;
    if (this._customInstances.length >= this._customMaxCount) return null;

    const key = `${groupId}:${this._customNextId++}`;
    this._customInstances.push({ key, groupId });

    // Set default variant
    const defaultVariant = variantId || (group.variants[0] && group.variants[0].id);
    if (defaultVariant) {
      this.state.layerSelections[key] = defaultVariant;
    }
    this.state.elementVisibility[key] = true;

    return key;
  },

  /**
   * Add a new custom layer of the given type.
   * Creates Konva group, adds to layerOrder, re-renders controls.
   */
  addCustomLayer(groupId) {
    if (!this._customLayersEnabled) return;
    if (this._customInstances.length >= this._customMaxCount) return;

    const key = this._createCustomInstance(groupId, null, false);
    if (!key) return;

    // Add to layerOrder (before text if present, otherwise at end)
    const textIdx = this.state.layerOrder.indexOf('text');
    if (textIdx >= 0) {
      this.state.layerOrder.splice(textIdx, 0, key);
    } else {
      this.state.layerOrder.push(key);
    }

    // Create Konva group
    const grp = new Konva.Group({ listening: true });
    this._layerOrderGroups[key] = grp;
    this.mainLayer.add(grp);

    // Sync and render
    this.syncKonvaOrder();
    this.renderOverlayGroup(key);
    this._hasReorderable = this.state.layerOrder.length >= 2;
    this.renderControls();
  },

  /**
   * Remove a specific custom layer instance.
   */
  removeCustomLayer(instanceKey) {
    const instIdx = this._customInstances.findIndex(i => i.key === instanceKey);
    if (instIdx < 0) return;

    // Remove from instances list
    this._customInstances.splice(instIdx, 1);

    // Clean up state
    delete this.state.layerSelections[instanceKey];
    delete this.state.elementVisibility[instanceKey];
    delete this._overlaySavedPos[instanceKey];
    delete this._customPremadeSelections[instanceKey];

    // Remove from layerOrder
    const orderIdx = this.state.layerOrder.indexOf(instanceKey);
    if (orderIdx >= 0) this.state.layerOrder.splice(orderIdx, 1);

    // Destroy Konva nodes
    const existingNode = this._overlayNodes[instanceKey];
    if (existingNode && this.transformer && this.transformer.nodes().includes(existingNode)) {
      this.transformer.nodes([]);
      this.transformerLayer.batchDraw();
    }
    const grp = this._layerOrderGroups[instanceKey];
    if (grp) grp.destroy();
    delete this._layerOrderGroups[instanceKey];
    delete this._overlayNodes[instanceKey];

    this.syncKonvaOrder();
    this._hasReorderable = this.state.layerOrder.length >= 2;
    this.renderControls();
    this.mainLayer.batchDraw();
  },

  /**
   * Render the "Add Layer" button with type picker dropdown.
   * @param {Array} html - HTML string array to push into
   */
  _renderCustomLayerControls(html) {
    if (!this._customLayersEnabled) return;

    const count = this._customInstances.length;
    const max = this._customMaxCount;
    const canAdd = count < max;
    const groups = this._customAllowedGroups
      .map(id => this.layerGroups.find(g => g.id === id))
      .filter(Boolean);

    html.push(`
      <div class="custom-layer-controls">
        <div class="custom-layer-controls__header">
          <span class="custom-layer-controls__badge">${count}/${max} layers</span>
        </div>
        <div class="custom-layer-add-wrapper">
          <button class="layer-add-btn" id="custom-layer-add-btn" ${!canAdd ? 'disabled title="Maximum reached"' : ''}>
            + Add Layer
          </button>
          <div class="custom-layer-dropdown" id="custom-layer-dropdown" style="display:none;">
            ${groups.map(g => `
              <button class="custom-layer-dropdown__item" data-add-custom-group="${g.id}">
                ${g.name}
              </button>
            `).join('')}
          </div>
        </div>
      </div>
    `);
  },

  /**
   * Render a custom layer instance card.
   * @param {Array} html - HTML string array
   * @param {Object} instance - { key, groupId }
   * @param {string} activePose - for resolving pose-specific src
   * @param {boolean} isFirst - first in display order
   * @param {boolean} isLast - last in display order
   */
  _renderCustomInstanceCard(html, instance, activePose, isFirst, isLast) {
    const { groupDef } = this._resolveGroupDef(instance.key);
    if (!groupDef) return;

    const selectedVariantId = this.state.layerSelections[instance.key];
    const variant = groupDef.variants.find(v => v.id === selectedVariantId);
    let thumbnailSrc = '';
    if (variant && variant.src) {
      const srcStr = typeof variant.src === 'object'
        ? (variant.src[activePose] || Object.values(variant.src)[0])
        : variant.src;
      thumbnailSrc = srcStr ? resolveAssetUrl(srcStr) : '';
    }
    const isVisible = this.state.elementVisibility[instance.key] !== false;
    const isExpandable = groupDef.variants && groupDef.variants.length > 1;
    const showReorder = this._hasReorderable;
    const dragHandle = showReorder ? `<div class="layer-card__drag-handle" data-drag-handle="${instance.key}" title="Drag to reorder">⠿</div>` : '';
    const slotAttr = showReorder ? ` data-layer-slot="${instance.key}"` : '';

    // Instance label: "Box #1", "Box Car #2", etc.
    const sameTypeInstances = this._customInstances.filter(i => i.groupId === instance.groupId);
    const typeIdx = sameTypeInstances.indexOf(instance) + 1;
    const label = sameTypeInstances.length > 1 ? `${groupDef.name} #${typeIdx}` : groupDef.name;

    html.push(`
      <div class="layer-card ${isExpandable ? 'layer-card--expandable' : ''} ${!isVisible ? 'layer-card--hidden' : ''} layer-card--custom" data-layer-card="custom_${instance.key}"${slotAttr}>
        <div class="layer-card__header" data-layer-toggle="custom_${instance.key}">
          ${dragHandle}
          <div class="layer-card__thumbnail">
            ${thumbnailSrc ? `<img src="${thumbnailSrc}" alt="${label}" />` : `<span class="layer-card__fallback-icon">📦</span>`}
          </div>
          <span class="layer-card__title">${label}</span>
          <div class="layer-card__actions">
            <button class="layer-card__btn layer-card__btn--remove" data-remove-custom="${instance.key}" title="Remove Layer">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <button class="layer-card__btn layer-card__btn--reset" data-group-reset-custom="${instance.key}" title="Reset Position">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
            </button>
            <button class="layer-card__btn layer-card__btn--visibility" data-group-toggle-btn-custom="${instance.key}" title="Toggle Visibility">
              ${isVisible
        ? `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
        : `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`
      }
            </button>
            ${isExpandable
              ? `<span class="layer-card__chevron"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg></span>`
              : `<span class="layer-card__chevron layer-card__chevron--placeholder" style="width: 16px; height: 16px; visibility: hidden; margin-left: 4px; display: inline-block; flex-shrink: 0;"></span>`
            }
          </div>
        </div>
        ${isExpandable ? `
        <div class="layer-card__body">
          <div class="asset-grid">
            ${groupDef.variants.map(v => {
              const srcStr = (v.src && typeof v.src === 'object') ? (v.src[activePose] || Object.values(v.src)[0]) : v.src;
              return `<div class="asset-item ${selectedVariantId === v.id ? 'asset-item--selected' : ''}" data-custom-layer-group="${instance.key}" data-variant-id="${v.id}"><div class="asset-item__preview">${srcStr ? `<img src="${resolveAssetUrl(srcStr)}" alt="${v.name}" loading="lazy" onerror="this.style.display='none'">` : '<span class="asset-item__none">✕</span>'}</div><span class="asset-item__name">${v.name}</span></div>`;
            }).join('')}
          </div>
          ${this._renderPremadeDropdown(instance, groupDef)}
        </div>` : ''}
      </div>
    `);
  },

  /**
   * Bind event handlers for custom layer controls.
   * Called from bindSceneControls().
   */
  _bindCustomLayerControls() {
    if (!this._customLayersEnabled) return;

    // Add layer button + dropdown toggle
    const addBtn = document.getElementById('custom-layer-add-btn');
    const dropdown = document.getElementById('custom-layer-dropdown');
    if (addBtn && dropdown) {
      addBtn.addEventListener('click', e => {
        e.stopPropagation();
        dropdown.style.display = dropdown.style.display === 'none' ? '' : 'none';
      });

      // Close dropdown on outside click
      document.addEventListener('click', () => {
        dropdown.style.display = 'none';
      }, { once: true });
    }

    // Dropdown type selection
    this.controlsPanel.querySelectorAll('[data-add-custom-group]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const groupId = e.currentTarget.dataset.addCustomGroup;
        this.addCustomLayer(groupId);
        if (dropdown) dropdown.style.display = 'none';
      });
    });

    // Remove custom layer
    this.controlsPanel.querySelectorAll('[data-remove-custom]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.removeCustomLayer(e.currentTarget.dataset.removeCustom);
      });
    });

    // Custom layer variant selection
    this.controlsPanel.querySelectorAll('[data-custom-layer-group]').forEach(item => {
      item.addEventListener('click', e => {
        const instanceKey = e.currentTarget.dataset.customLayerGroup;
        const variantId = e.currentTarget.dataset.variantId;
        this.state.layerSelections[instanceKey] = variantId;

        // Update selection UI
        this.controlsPanel.querySelectorAll(`[data-custom-layer-group="${instanceKey}"]`).forEach(el =>
          el.classList.remove('asset-item--selected')
        );
        e.currentTarget.classList.add('asset-item--selected');

        // Update card thumbnail
        const { groupDef } = this._resolveGroupDef(instanceKey);
        const card = this.controlsPanel.querySelector(`[data-layer-card="custom_${instanceKey}"]`);
        const variant = groupDef && groupDef.variants.find(v => v.id === variantId);
        if (card && variant && variant.src) {
          const img = card.querySelector('.layer-card__thumbnail img');
          if (img) img.src = resolveAssetUrl(variant.src);
        }

        this.renderOverlayGroup(instanceKey);
      });
    });

    // Custom layer visibility toggle
    this.controlsPanel.querySelectorAll('[data-group-toggle-btn-custom]').forEach(btn => {
      btn.addEventListener('click', e => {
        const key = e.currentTarget.dataset.groupToggleBtnCustom;
        const isVisible = this.state.elementVisibility[key] === false;
        this.state.elementVisibility[key] = isVisible;

        const card = this.controlsPanel.querySelector(`[data-layer-card="custom_${key}"]`);
        if (card) {
          card.classList.toggle('layer-card--hidden', !isVisible);
          e.currentTarget.innerHTML = isVisible
            ? `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`
            : `<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>`;
        }

        this.renderOverlayGroup(key);
      });
    });

    // Custom layer reset
    this.controlsPanel.querySelectorAll('[data-group-reset-custom]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const key = e.currentTarget.dataset.groupResetCustom;
        const { groupDef } = this._resolveGroupDef(key);
        if (groupDef && groupDef.variants.length > 0) {
          this.state.layerSelections[key] = groupDef.variants[0].id;
          delete this._overlaySavedPos[key];
          this.renderOverlayGroup(key);
          this.renderControls();
        }
      });
    });

    // Premade dropdown selection for box/box_car overlays
    this.controlsPanel.querySelectorAll('[data-premade-select]').forEach(select => {
      select.addEventListener('change', e => {
        const instanceKey = e.currentTarget.dataset.premadeSelect;
        const value = e.currentTarget.value;

        if (!value) {
          // "None" selected
          this.clearCustomPremade(instanceKey);
        } else {
          // Parse value: "char:naruto" or "car:ae86_v1:demo-assets/cars/AE86_1.png"
          const parts = value.split('::');
          const type = parts[0]; // 'character' or 'car'
          const id = parts[1];
          const src = parts[2];
          this.setCustomPremade(instanceKey, { type, id, src });
        }

        // Update preview thumbnail next to dropdown
        const previewEl = this.controlsPanel.querySelector(`[data-premade-preview="${instanceKey}"]`);
        if (previewEl) {
          const opt = e.currentTarget.selectedOptions[0];
          const thumbSrc = opt ? opt.dataset.thumb : '';
          if (thumbSrc) {
            previewEl.innerHTML = `<img src="${resolveAssetUrl(thumbSrc)}" alt="Preview" />`;
          } else {
            previewEl.innerHTML = '<span class="premade-dropdown__preview-empty">—</span>';
          }
        }
      });
    });
  },

  /**
   * Render a premade character/car dropdown for a custom layer instance.
   * Returns empty string if the group doesn't support premade overlays.
   */
  _renderPremadeDropdown(instance, groupDef) {
    const premadeOverlay = groupDef.premadeOverlay;
    if (!premadeOverlay) return '';

    const catalog = premadeOverlay.catalog; // 'characters' or 'cars'
    const currentSelection = this._customPremadeSelections[instance.key];
    const currentId = currentSelection ? currentSelection.id : '';

    // Build options based on catalog type
    let options = '';
    let currentThumb = '';

    if (catalog === 'characters' && typeof PREMADE_CHARACTERS !== 'undefined') {
      options = PREMADE_CHARACTERS.map(c => {
        const selected = currentId === c.id ? ' selected' : '';
        const thumbSrc = c.thumb || c.src;
        if (selected) currentThumb = thumbSrc;
        return `<option value="character::${c.id}::${c.src}" data-thumb="${thumbSrc}"${selected}>${c.name}</option>`;
      }).join('');
    } else if (catalog === 'cars' && typeof PREMADE_CARS !== 'undefined') {
      // Flatten car models + variants into individual options
      options = PREMADE_CARS.map(model => {
        return model.variants.map(v => {
          const displayName = model.variants.length > 1 ? `${model.name} — ${v.name}` : model.name;
          const selected = currentId === v.id ? ' selected' : '';
          const thumbSrc = v.thumb || v.src;
          if (selected) currentThumb = thumbSrc;
          return `<option value="car::${v.id}::${v.src}" data-thumb="${thumbSrc}"${selected}>${displayName}</option>`;
        }).join('');
      }).join('');
    }

    const label = catalog === 'characters' ? 'Character in Box' : 'Car in Box';
    const previewHtml = currentThumb
      ? `<img src="${resolveAssetUrl(currentThumb)}" alt="Preview" />`
      : '<span class="premade-dropdown__preview-empty">—</span>';

    return `
      <div class="premade-dropdown">
        <label class="premade-dropdown__label">${label}</label>
        <div class="premade-dropdown__row">
          <div class="premade-dropdown__preview" data-premade-preview="${instance.key}">
            ${previewHtml}
          </div>
          <div class="premade-dropdown__select-wrap">
            <select class="premade-dropdown__select" data-premade-select="${instance.key}">
              <option value="">None</option>
              ${options}
            </select>
            <span class="premade-dropdown__arrow"></span>
          </div>
        </div>
      </div>
    `;
  },

  /**
   * Resolve and set initial custom premade selection from design config values.
   */
  _setInitialPremadeFromConfig(key, groupId, premadeId) {
    const { groupDef } = this._resolveGroupDef(key);
    if (!groupDef || !groupDef.premadeOverlay) return;

    const catalog = groupDef.premadeOverlay.catalog;
    if (catalog === 'characters' && typeof PREMADE_CHARACTERS !== 'undefined') {
      const c = PREMADE_CHARACTERS.find(ch => ch.id === premadeId);
      if (c) {
        this._customPremadeSelections[key] = { type: 'character', id: c.id, src: c.src };
      }
    } else if (catalog === 'cars' && typeof PREMADE_CARS !== 'undefined') {
      for (const model of PREMADE_CARS) {
        const v = model.variants.find(varnt => varnt.id === premadeId);
        if (v) {
          this._customPremadeSelections[key] = { type: 'car', id: v.id, src: v.src };
          break;
        }
      }
    }
  }
});
